import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  buildEssentialConsentCategories,
  type ConsentCategories,
  type ConsentCategory,
  type ConsentData,
} from "@/lib/consent.utils";
import { cn } from "@/lib/utils";

interface ConsentModalProps {
  open: boolean;
  consentData: ConsentData | null;
  onOpenChange: (open: boolean) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onSave: (categories: Partial<ConsentCategories>) => void;
}

const categoryCopy: Array<{
  key: ConsentCategory;
  title: string;
  description: string;
  fixed?: boolean;
}> = [
  {
    key: "essential",
    title: "Essenciais",
    description: "Sessão, autenticação, segurança e funcionamento básico do app.",
    fixed: true,
  },
  {
    key: "functional",
    title: "Funcionais",
    description: "Preferências de cidade, filtros, tema e ajustes que deixam o uso mais fluido.",
  },
  {
    key: "analytics",
    title: "Analytics",
    description: "Eventos de uso, funil e páginas para entendermos onde o produto precisa melhorar.",
  },
  {
    key: "monitoring",
    title: "Monitoramento",
    description: "Erros e performance com Sentry. Sem permissão, mantemos apenas um modo mínimo sem identificação.",
  },
  {
    key: "marketing",
    title: "Marketing",
    description: "Pixels e UTMs para campanhas futuras. Nada de marketing é carregado nesta versão sem sua permissão.",
  },
];

export default function ConsentModal({
  open,
  consentData,
  onOpenChange,
  onAcceptAll,
  onRejectAll,
  onSave,
}: ConsentModalProps) {
  const initialCategories = useMemo(
    () => consentData?.categories ?? buildEssentialConsentCategories(),
    [consentData],
  );
  const [draftCategories, setDraftCategories] = useState<ConsentCategories>(initialCategories);

  useEffect(() => {
    if (open) {
      setDraftCategories(initialCategories);
    }
  }, [initialCategories, open]);

  const updateCategory = (category: ConsentCategory, checked: boolean) => {
    if (category === "essential") return;
    setDraftCategories((current) => ({
      ...current,
      essential: true,
      [category]: checked,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gerenciar privacidade</DialogTitle>
          <DialogDescription>
            Escolha quais categorias opcionais podem ser usadas. O app continua funcionando com
            apenas os itens essenciais.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {categoryCopy.map((category) => {
            const checked = draftCategories[category.key];
            const switchId = `consent-${category.key}`;

            return (
              <div
                key={category.key}
                className={cn(
                  "rounded-[18px] border border-[#dfe9e3] bg-[#f8fbf9] p-4",
                  category.fixed && "bg-[#eef8f1]",
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <label
                      htmlFor={switchId}
                      className="text-sm font-black tracking-[-0.01em] text-[#102018]"
                    >
                      {category.title}
                    </label>
                    <p className="mt-1 text-sm leading-6 text-[#607269]">{category.description}</p>
                  </div>
                  <Switch
                    id={switchId}
                    checked={checked}
                    disabled={category.fixed}
                    aria-label={category.title}
                    onCheckedChange={(nextChecked) => updateCategory(category.key, nextChecked)}
                  />
                </div>
                {category.fixed ? (
                  <p className="mt-2 text-xs font-semibold text-[#16783a]">
                    Sempre ativo para segurança e funcionamento.
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>

        <p className="text-sm leading-6 text-muted-foreground">
          Veja a{" "}
          <a className="font-semibold text-primary underline-offset-4 hover:underline" href="/privacidade">
            Política de Privacidade
          </a>{" "}
          completa e a{" "}
          <a className="font-semibold text-primary underline-offset-4 hover:underline" href="/privacidade/cookies">
            Política de Cookies
          </a>
          .
        </p>

        <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" variant="outline" onClick={onRejectAll}>
              Usar apenas essenciais
            </Button>
            <Button type="button" variant="outline" onClick={onAcceptAll}>
              Aceitar todos
            </Button>
          </div>
          <Button type="button" onClick={() => onSave(draftCategories)}>
            Salvar preferências
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
